import { CGV2LocationOutDTO } from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, LineRoute, LineRouteStop, LocationLockMode, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';

/**
 * Both-direction Linka (RD-2978): the route can be driven from its last stop to its first one at the same contracted
 * prices. The first stop of the form offers the locations of both ends; picking one that only the last stop has turns
 * the route around, picking one of the first stop again turns it back.
 *
 * What belongs to a place travels with its stop: candidates and lock, the weekly windows (tmplV2StopId), the loading
 * reference and requirements. The cargo stays where it is in the route: orders are loaded at the (new) first stop
 * and every stop keeps its unloadings.
 */

const ids = (stop: LineRouteStop) => stop.candidates.map(c => c.locationId);

const lockFor = (count: number): LocationLockMode => (count === 1 ? 'LOCKED' : count > 1 ? 'LIMITED' : 'FREE');

/** Locations the first stop of a both-direction route offers: the first stop's and the last stop's, in that order. */
export const firstStopChoices = (route: LineRoute): string[] =>
  route.stops.length < 2 ? [] : Array.from(new Set([...ids(route.stops[0]), ...ids(route.stops[route.stops.length - 1])]));

/** Fills a stop from an address-book location the way picking it in the select does. */
export const applyLocationToLocality = (locality: ExpeditionLocality, location: CGV2LocationOutDTO, stopLocationNote?: string) => {
  const firstContact = location.contacts?.[0];
  locality.location = {
    ...location,
    contacts: location.contacts?.filter(contact => !!contact.contactEmail?.trim()) || [],
    locationNote: stopLocationNote?.trim() ? stopLocationNote : location.locationNote,
  };
  locality.companyLocationId = location.locationId;
  locality.timezoneId = location.timezoneId;
  locality.timeslots = { ...locality.timeslots, timezoneId: location.timezoneId };
  if (firstContact) {
    locality.selectedPerson = {
      contactEmail: firstContact.contactEmail?.trim() || null,
      contactName: firstContact.contactName?.trim() || null,
      contactPhone: firstContact.contactPhone?.trim() || null,
    };
  }
  locality.additionalContacts = location.contacts?.filter(contact => !!contact.contactEmail).filter(contact => contact.automaticPrefillToNotify) || [];
};

const clearLocation = (locality: ExpeditionLocality) => {
  locality.location = null;
  locality.companyLocationId = null;
  locality.selectedPerson = undefined;
  locality.additionalContacts = [];
};

/** Lays the stops of [route] onto the form in the given direction; position 0 offers both ends. */
const layOut = (draft: NewExpeditionForm, route: LineRoute, reversed: boolean) => {
  const n = route.stops.length;
  draft.locations.forEach((locality, position) => {
    const stop = reversed ? route.stops[n - 1 - position] : route.stops[position];
    locality.tmplV2StopId = stop.tmplV2StopId;
    locality.tmplV2StopHasOpeningHours = stop.tmplV2StopHasOpeningHours;
    locality.tmplV2StopLocationNote = stop.locationNote?.trim() ? stop.locationNote : undefined;
    locality.loadingReference = stop.loadingReference;
    locality.requirements = stop.requirements?.length ? [...stop.requirements] : undefined;
    // the windows belong to the stop: picked days are re-filled for the new place
    locality.timeslots = { dayWithInterval: [], timezoneId: locality.timezoneId };
    locality.supersededFromLocationName = undefined;

    if (position === 0) {
      const choices = firstStopChoices(route);
      locality.locationLock = lockFor(choices.length);
      locality.allowedLocationIds = choices.length > 1 ? choices : undefined;
      return;
    }

    const candidateIds = ids(stop);
    const lock = lockFor(candidateIds.length);
    locality.locationLock = lock;
    locality.allowedLocationIds = lock === 'LIMITED' ? candidateIds : undefined;
    if (lock === 'LOCKED') {
      applyLocationToLocality(locality, stop.candidates[0], stop.locationNote);
    } else if (!locality.companyLocationId || (lock === 'LIMITED' && !candidateIds.includes(locality.companyLocationId)) || lock === 'FREE') {
      clearLocation(locality);
    }
  });
};

/**
 * Called when the first stop gets [pickedLocationId]: turns the route when the location belongs only to the other
 * end. Returns true when the direction changed. Mutates [draft] (use inside a produce).
 */
export const orientLineRoute = (draft: NewExpeditionForm, pickedLocationId: string): boolean => {
  const route = draft.lineRoute;
  if (!route || route.stops.length < 2 || draft.locations.length !== route.stops.length || !pickedLocationId) return false;

  const firstIds = ids(route.stops[0]);
  const lastIds = ids(route.stops[route.stops.length - 1]);
  let reversed = route.reversed;
  if (!reversed && lastIds.includes(pickedLocationId) && !firstIds.includes(pickedLocationId)) reversed = true;
  else if (reversed && firstIds.includes(pickedLocationId) && !lastIds.includes(pickedLocationId)) reversed = false;
  if (reversed === route.reversed) return false;

  route.reversed = reversed;
  layOut(draft, route, reversed);
  return true;
};

/** Prefill of a both-direction Linka: forward, with the first stop offering both ends. */
export const initLineRoute = (draft: NewExpeditionForm, stops: LineRouteStop[]) => {
  draft.lineRoute = { stops, reversed: false };
  const first = draft.locations[0];
  const choices = firstStopChoices(draft.lineRoute);
  first.locationLock = lockFor(choices.length);
  first.allowedLocationIds = choices.length > 1 ? choices : undefined;
};
